import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { eligibilityActions, readEligibilities } from './eligibility/eligibilityData';
import { buildRowItems, ELIGIBILITY_LABEL, eligibilityRowDisplay, hiddenWhileQuiet } from './CategoryCard';
import { concludedByDenial, isQuietlyReady } from './referralProgress';
import StatusIcon, { AT_REST_TEXT, rollupIndicator, StatusIndicator, statusIndicator } from './StatusIcon';

interface RailRow {
  key: string;
  label: string;
  indicator: StatusIndicator;
}

interface RailStep extends RailRow {
  // Shown nested only when a category has more than one action (design §12 roll-up).
  children: RailRow[];
}

// Collapses every per-building fan-out to at most one entry per KIND, which is what the rail wants:
// the buildings live in the category's card, and a rail row per building just repeats one step label
// N times.
//
// Per KIND rather than per category, which is the bug this replaces. The old guard asked whether the
// WHOLE category was fanned (`actions.every(groupId !== null)`), so a category mixing a shared step
// with a fanned one — Referral Outcome beside the per-building EHR sync — failed open and built one
// child per building anyway.
// Every row the card would render, fan-out siblings included — what the category's own dot rolls
// up over. `railActions` below narrows this further to what gets a child row.
function visibleActions(actions: WorkflowAction[]): WorkflowAction[] {
  return actions.filter((action) => !hiddenWhileQuiet(action));
}

function railActions(actions: WorkflowAction[]): WorkflowAction[] {
  const seenFannedKinds = new Set<string>();
  return actions.filter((action) => {
    // A row the card leaves out gets no rail dot either, or the rail would count a child nobody
    // can see (OLIO-3158).
    if (hiddenWhileQuiet(action)) return false;
    if (action.groupId === null) return true;
    if (seenFannedKinds.has(action.kind)) return false;
    seenFannedKinds.add(action.kind);
    return true;
  });
}

// One row per category, with action children only where a category has more than
// one action. A category that is entirely per-building copies of a step (fanned out,
// OLIO-2611) gets no children — the buildings live in the category's card, and a rail
// row per building would just repeat the same step label N times. The Payer Search +
// Insurance Verification pair collapses into one "Insurance Eligibility Check" child, and
// its combined state folds into the category icon too (OLIO-2301 — mirrors CategoryCard).
function buildSteps(workflow: Workflow): RailStep[] {
  return workflow.actionsByCategory().map(({ category, actions }) => {
    return {
      key: category.key,
      label: category.name,
      // Each eligibility action folds in via its API-derived effectiveStatus (rollupIndicator reads
      // it), so the category icon agrees with the collapsed eligibility child below — no stand-in.
      //
      // Over the visible rows, mirroring the card's badge: a row hidden while it is quiet gets no
      // rail child, so it must not vote on the category's own dot either, or the dot and the badge
      // disagree in exactly the case the hiding rule exists for (OLIO-3158). Hidden-filtered only,
      // NOT `railActions` — that folds a fan-out to one representative, and a rollup wants every
      // building's status to count.
      //
      // A denial concludes two categories (OLIO-3170) that would otherwise roll up green Complete;
      // the same predicate the card badge uses, so the icon and the badge cannot disagree.
      indicator: concludedByDenial(workflow, actions)
        ? 'denied'
        : visibleActions(actions).length === 1 && isQuietlyReady(visibleActions(actions)[0])
          ? 'review'
          : rollupIndicator(visibleActions(actions)),
      children:
        railActions(actions).length > 1
          ? buildRowItems(railActions(actions)).map((item) => {
              if (item.kind === 'eligibility') {
                // Rolls up over every coverage, matching the collapsed row in the card.
                return {
                  key: 'eligibility',
                  label: ELIGIBILITY_LABEL,
                  indicator: eligibilityRowDisplay(eligibilityActions(readEligibilities(workflow))).indicator,
                };
              }
              // A child the denial concludes (the Referral Outcome row itself) reads denied like its
              // category, not the green check its `completed` status would give it — the row and the
              // category above it must not tell two stories. Its siblings (the EHR sync) keep their own.
              return {
                key: item.action.id,
                label: item.action.displayLabel,
                indicator: concludedByDenial(workflow, [item.action]) ? 'denied' : statusIndicator(item.action),
              };
            })
          : [],
    };
  });
}

// Steps only — a substep never bolds regardless of its own state. Denied bolds like Complete: it is
// a concluded step, not a waiting one.
const isBoldStep = (indicator: StatusIndicator) =>
  indicator === 'running' || indicator === 'complete' || indicator === 'review' || indicator === 'denied';

export default function ProgressRail({ workflow }: { workflow: Workflow }) {
  const steps = buildSteps(workflow);

  return (
    <Rail data-testid='progress-rail'>
      <BodySmallBold>Referral Progress</BodySmallBold>
      {workflow.createdAt && (
        <LastUpdate>
          <LastUpdateLabel>Last Update:</LastUpdateLabel> Referral created on {simpleDateAtTime(workflow.createdAt)}
        </LastUpdate>
      )}
      <Steps>
        {steps.map((step) => (
          <div key={step.key}>
            <StepRow data-testid={`rail-step-${step.key}`}>
              <StatusIcon indicator={step.indicator} size={18} />
              <StepLabel $bold={isBoldStep(step.indicator)} $muted={step.indicator === 'muted'}>
                {step.label}
              </StepLabel>
            </StepRow>
            {step.children.map((child) => (
              <ChildRow key={child.key} data-testid={`rail-child-${child.key}`}>
                <StatusIcon indicator={child.indicator} size={12} />
                <ChildLabel $muted={child.indicator === 'muted'}>{child.label}</ChildLabel>
              </ChildRow>
            ))}
          </div>
        ))}
      </Steps>
    </Rail>
  );
}

const Rail = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};
  padding: 20px;
`;

const LastUpdate = styled(BodySmall)`
  display: block;
  margin-top: 12px;
  font-size: 12px;
  color: ${colors.black50};
`;

const LastUpdateLabel = styled.span`
  font-weight: 500;
  color: ${colors.black75};
`;

const Steps = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-left: 28px;
`;

// The rail tells state apart by weight, not color — every label is black100 whatever its status, so
// a purple "processing" word doesn't compete with the spinner beside it. At-rest is the one
// exception (OLIO-3270): Not Applicable and Canceled recede, so a step with nothing to do doesn't
// read as loudly as one still to come. The shade comes from AT_REST_TEXT rather than being restated
// here, so retuning it moves the rail and the card rows together.
const StepLabel = styled(BodySmall)<{ $bold: boolean; $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? AT_REST_TEXT : colors.black100)};
  font-weight: ${({ $bold }) => ($bold ? 500 : 400)};
`;

const ChildLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? AT_REST_TEXT : colors.black100)};
  font-size: 12px;
`;
